<template>
    <div class="mt-3 mx-2">
        <div class="listing-card card p-4 m-auto">
            <h2>{{ $t(`orders.${$route.params.offerType}.title`) }}</h2>

            <div class="table-responsive">
                <table class="table table-bordered">
                    <thead>
                        <tr ref="tableHeader">
                            <th>
                                <b-link href="#" data-col="number" @click="sort">
                                    #
                                </b-link>
                            </th>
                            <th>
                                {{ $t('orders.status') }}
                            </th>
                            <th>
                                <b-link href="#" data-col="start" class="ordered-desc" @click="sort">
                                    {{ $t('orders.departure_date') }}
                                </b-link>
                            </th>
                            <th>
                                {{ $t('orders.route') }}
                            </th>
                            <th v-if="$route.params.offerType !== 'berbus'">
                                {{ $t('orders.return_departure_date') }}
                            </th>
                            <th v-if="$route.params.offerType !== 'berbus'">
                                {{ $t('orders.return_route') }}
                            </th>
                            <th>{{ $t('orders.actions') }}</th>
                        </tr>
                    </thead>
                    <tbody v-if="orders.length && !isLoading">
                        <tr
                            v-for="order in orders"
                            :key="order.order_number"
                        >
                            <td scope="row">
                                {{ order.order_number }}
                            </td>
                            <td>
                                <order-status-badge :status="order.status" />
                            </td>
                            <td>
                                {{ formatDateTime(order.departure_date) }}
                            </td>
                            <td>
                                {{ getStartPlace(order) }}
                                <font-awesome-icon icon="chevron-right" class="mx-2" />
                                {{ getEndPlace(order) }}
                            </td>
                            <td v-if="!isOrderBerbus(order)">
                                <span v-if="order.has_return">
                                    {{ formatDateTime(order.return_departure_date) }}
                                </span>
                                <span v-else>
                                    ----
                                </span>
                            </td>
                            <td v-if="!isOrderBerbus(order)">
                                <span v-if="order.has_return">
                                    {{ order.return_start_place }}
                                    <font-awesome-icon icon="chevron-right" class="mx-2" />
                                    {{ order.return_end_place }}
                                </span>
                                <span v-else>
                                    ----
                                </span>
                            </td>
                            <td>
                                <router-link class="btn btn-success rounded-0" :to="{name: 'users_orders_show', params: {offerType: order.offer_type, orderNumber: order.id}}">
                                    {{ $t('orders.details') }}
                                </router-link>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr>
                            <td colspan="7" class="text-center">
                                <div class="justify-content-center pt-4" v-if="isLoading">
                                    <div class="spinner-border" role="status">
                                        <span class="sr-only">{{ $t('loading') }}...</span>
                                    </div>
                                </div>
                                <div v-else>
                                    {{ $t('orders.no_orders') }}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="text-center">
                <pagination :data="ordersPaginationData" :align="center" @pagination-change-page="getOrders" />
            </div>
        </div>
    </div>
</template>

<script>
    import OrderStatusBadge from './components/OrderStatusBadge';
    import backofficeRepository from '@repositories/BackofficeRepository';
    import toastr from 'toastr';

    export default {
        components: {
            OrderStatusBadge,
        },

        data() {
            return {
                orders: {},
                ordersPaginationData: {},
                center: 'center',
                sortedBy: 'start',
                sortedDesc: true,
                per_page: 10,
                isLoading: true,
                offer_type: this.$route.params.offerType,
                order_types: {
                    BUS_LINE: 'at',
                    BUSINESS_TAXI: 'bt',
                    BERBUS: 'berbus',
                    FAMILY_AIRPORT_TRANSPORT: 'fat',
                },
            };
        },

        computed: {
            sortedType() {
                return this.sortedDesc?'desc':'asc';
            },
        },

        watch: {
            $route(to, from) {
                this.offer_type = to.params.offerType;
                this.getOrders();
            },
        },

        created() {
            backofficeRepository.isTokenValid().then((isTokenValid) => {
                if(!isTokenValid){
                    this.$store.dispatch('user/unsetUser');
                    toastr.error(this.$t('auth.session_expired'));
                    this.$router.push({ name: 'login.index', query: {redirectTo: this.$route.fullPath} });
                    return;
                }
                this.getOrders();
            });
        },

        methods: {
            getOrders(page = 1) {
                this.isLoading = true;
                const offset = (page - 1) * this.per_page;
                const limit = this.per_page;
                const type = Object.keys(this.order_types).find(key => this.order_types[key] === this.offer_type);
                const orderBy = {[this.sortedBy]: this.sortedDesc ? 'DESC' : 'ASC'};
                backofficeRepository.clientPlannerRidesList(limit, offset, 0, 0, {type: type}, orderBy)
                    .then(({data}) => {
                        this.orders = data.clientPlannerRidesList.clientPlannerRidesList.edges.map(edge => {
                            return {
                                id: edge.node.order.id,
                                order_number: edge.node.order.number,
                                status: edge.node.order.status,
                                departure_date: edge.node.start*1000,
                                return_departure_date: edge.node.end*1000,
                                start_place: edge.node.startStop.address,
                                end_place: edge.node.endStop.address,
                                return_start_place: edge.node.endStop.address,
                                return_end_place: edge.node.startStop.address,
                                has_return: edge.node.rideStops.length>2,
                                new_offer_type: edge.node.type,
                                offer_type: this.order_types[edge.node.type],
                            };
                        }).filter(order => order.offer_type === this.offer_type);
                        this.ordersPaginationData = {
                            links: {
                                first: null,
                                last: null,
                                prev: null,
                                next: null,
                            },
                            meta: {
                                current_page: page,
                                from: null,
                                last_page: Math.ceil(data.clientPlannerRidesList.clientPlannerRidesList.totalCount/this.per_page),
                                path: null,
                                per_page: this.per_page,
                                to: null,
                                total: data.clientPlannerRidesList.clientPlannerRidesList.totalCount,
                            },
                        };

                        this.isLoading = false;
                    });
            },

            sort(event) {
                const el = event.srcElement;
                //clear current ordered columns
                this.$refs.tableHeader.getElementsByTagName('a').forEach(function(el){
                    el.classList.remove('ordered-desc');
                    el.classList.remove('ordered-asc');
                });

                if(this.sortedBy === el.dataset.col){
                    this.sortedDesc = !this.sortedDesc;
                }
                else{
                    this.sortedDesc = true;
                }
                el.classList.add('ordered-' + this.sortedType);
                this.sortedBy = el.dataset.col;
                this.getOrders();
            },

            getStartPlace(order) {
                return order.start_place;
            },

            getEndPlace(order) {
                return order.end_place;
            },

            isOrderBerbus(order) {
                return order.offer_type === 'berbus';
            },
        },
    };
</script>

<style lang="scss">
    $color-normal: #5f6a70;
    $color-active: #75100c;

    table th a{
        display: block;
        position: relative;
        color: $color-normal;
        text-decoration: underline;

        &.ordered-desc, &.ordered-asc{
            color: $color-active;
        }

        &.ordered-desc{
            &:before{
                color: $color-active;
            }
        }

        &.ordered-asc{
            &:after{
                color: $color-active;
            }
        }

        &:before{
            content: '▼';
            position: absolute;
            right: 0;
            color: $color-normal;
        }
        &:after{
            content: '▲';
            position: absolute;
            right: 12px;
            color: $color-normal;
        }
    }
</style>
<style scoped>
    .listing-card {
        max-width: 1300px;
    }

    .listing-card td, .listing-card th {
        font-size: 0.85rem;
    }
</style>
